/**
 * Générateurs des ITEMS NATIONAUX : français, 4e, Compréhension de l'écrit Petit Chose, campagne 2026.
 * Même contrat que les modules de maths (2026/6e/automatismes.js, 2026/4e/problemes.js) : un générateur
 * par item, sa fiche en tête (lue sur la CAPTURE de l'item), ITEMS avec la bonne réponse officielle
 * exacte (garde-fou du registre), CATEGORIES, generate.
 *
 * Une variante de compréhension ne change pas des valeurs : elle change de TEXTE. Le module porte UN
 * nouvel extrait, « Le Copiste d'Ombreval », écrit pour TanoEd à la manière d'un roman du XIXe siècle et
 * construit comme celui de septembre (Daudet, Le Petit Chose), page pour page : un jeune narrateur arrive
 * le soir, sous la pluie, dans une petite ville des Ardennes, pour être embauché comme copiste à la
 * bibliothèque, installée dans une vieille abbaye ; le portier le prend pour un écolier venu lire ; le
 * bibliothécaire, décrit en page 4, le trouve trop jeune mais le garde grâce à la lettre d'introduction
 * du proviseur ; l'archiviste lui sourit, mais sa tabatière « parle » et le menace ; le nom du narrateur
 * n'apparait que dans la dernière réplique du bibliothécaire.
 * Le texte est lu dans la liseuse (elements_additionnels.support_lecture, utils/supportLecture.js) : six
 * pages, chacune dans le rôle de la page de septembre (Q04 renvoie à la page 4, Q05 à la page 3) ; la
 * source termine la page 6, comme celle de Daudet.
 * VARIANTES_FIXES : une variante par item, sur ce texte (accepté par le harnais, evalnat.qa.test.js) ;
 * seul l'ordre des choix d'un QCM change d'un tirage à l'autre.
 *
 * Chaque question est celle de septembre posée sur le nouveau texte : même attendu, même type, mêmes
 * pièges. Les choix qui nomment le texte (titres, région, personnages, noms) sont refaits sur le nouveau
 * texte, chaque mauvaise réponse fabriquée pour la même raison que l'officielle. Les choix qui ne nomment
 * rien du texte (adjectifs du lieu, émotions, motifs d'embauche, contenu de la lettre, atmosphères) restent
 * ceux de l'item, avec les mêmes réponses : mode auto, TRANCHÉ LE 11/10 (on garde : même attendu, même
 * complexité ; la réponse de septembre peut revenir, décision du prof « Garde » du 10/10) (Q03, Q05, Q06,
 * Q07, Q09).
 * Second chemin (REGLES_EQUIPE § 3) : chaque citation est relue dans la page qu'elle cite, chaque appui
 * d'une réponse cherché dans le texte, chaque nom compté ; un texte retouché qui ne porte plus sa
 * question lève une erreur au lieu de partir.
 * Une personne nommée n'est jamais reprise par « il » ou « elle » dans une question (le récit n'est pas
 * concerné). Orthographe rectifiée, comme le sujet du ministère (chaine, s'il vous plait).
 */
import { randomChoice, makeQcm, makeVraiFaux } from '../../../../utils';
import { avecSupportLecture } from '../../../../../utils/supportLecture';

export const VARIANTES_FIXES = true;

const opts = (label) => ({ niveau: '4eme', matiere: 'francais', attendu: null, label });

/** Les paragraphes d'une page, en texte brut, séparés par une ligne vide (la forme des pages du livre, LivreImmersif). */
const paragraphes = (...p) => p.join('\n\n');

export const SUPPORT = {
    id: 'petit-chose-2026-variante-1',
    titre: 'Le Copiste d\'Ombreval',
    pages: [
        {
            page: 1,
            title: 'Texte littéraire : Le Copiste d\'Ombreval',
            content: paragraphes(
                `Le narrateur vient de quitter le lycée. Son père, imprimeur à Reims, est mort ruiné : le jeune homme doit gagner sa vie. Il se rend donc à Ombreval, pour être embauché comme « copiste » à la bibliothèque de la ville, c'est-à-dire employé chargé de recopier à la main les vieux registres…`,
                `Ombreval est une petite ville des Ardennes, tapie au fond d'une boucle de la Meuse, entre deux collines si noires de forêts que le soleil n'y descend guère plus d'une heure par jour ; le reste du temps, le brouillard de la rivière s'accroche aux toits comme une laine mouillée.`,
                `Le soir où j'y arrivai, il pleuvait depuis l'aube. Tassé tout en haut de la diligence, entre deux paniers de volailles, je grelottais dans mon manteau trop mince.`,
                `Sur la place, personne. Seul un chien mouillé me regarda descendre, puis s'éloigna sans aboyer, comme si je n'en valais pas la peine.`,
                `Sans prendre le temps de me sécher, je demandai au cocher le chemin de la bibliothèque : il me tardait de commencer à gagner mon pain.`,
            ),
        },
        {
            page: 2,
            title: '',
            content: paragraphes(
                `Ma malle sur l'épaule, je montai une ruelle en escalier et m'arrêtai devant un porche massif, que surmontait une statue de saint dont la pluie avait rongé le visage. Derrière, pas une fenêtre éclairée : tout semblait endormi depuis cent ans.`,
                `Je tirai la chaine d'une cloche. Elle tinta une seule fois, d'une voix fêlée et grincheuse, comme si je l'avais tirée de son sommeil. Un verrou grinça, le lourd battant s'entrouvrit ; à peine étais-je entré qu'il retomba derrière moi avec un bruit sourd, et j'attendis seul, dans le noir.`,
                `Enfin une lanterne parut au fond d'une cour : un gros homme en sabots, au nez rouge, sans doute le portier, me la mit sous le menton en bâillant.`,
                `– Vous venez pour lire ? La salle ferme à quatre heures, mon garçon. Revenez demain.`,
                `Il me prenait pour un écolier…`,
                `– Je ne viens pas pour lire. Je suis le nouveau copiste ; conduisez-moi, s'il vous plait, chez M. le bibliothécaire.`,
                `Le portier haussa les épaules et se mit en marche. Je le suivis.`,
                `Jamais je n'avais vu une maison aussi vaste…`,
            ),
        },
        {
            page: 3,
            title: '',
            content: paragraphes(
                `Nous suivîmes des couloirs qui n'en finissaient pas, nous montâmes des escaliers de pierre creusés par les pas des siècles, nous traversâmes des salles hautes comme des églises, aux murs noircis par la fumée. Partout le même froid, la même ombre, que la lanterne perçait à peine ; pas une voix, pas un bruit, sinon nos pas, que l'écho répétait derrière nous comme si quelqu'un nous suivait.`,
                `Chemin faisant, mon guide m'expliqua que la maison avait été une abbaye, où des moines avaient recopié des livres pendant six cents ans sans desserrer les dents.`,
                `Devant une porte basse, garnie de gros clous, M. Gobert ôta son bonnet et gratta trois fois contre le bois.`,
                `– Qu'on entre, et qu'on ferme ! glapit une voix aigre.`,
                `Au fond d'une longue pièce tapissée de livres, un homme écrivait à la lueur d'une chandelle presque usée.`,
                `– Monsieur le bibliothécaire, dit le portier, voici le nouveau copiste, qui vient remplacer M. Thévenin.`,
                `– C'est bon, grogna le bibliothécaire sans lever le nez de son papier.`,
                `Le portier se retira. Je restai debout près de la porte, immobile au bord du tapis, à tordre un bouton de ma veste, qui ne tenait plus qu'à un fil.`,
            ),
        },
        {
            page: 4,
            title: '',
            content: paragraphes(
                `Quand il eut signé sa page et jeté une pincée de sable dessus pour en sécher l'encre, le bibliothécaire releva la tête, et je pus enfin voir son visage, jaune et ridé comme un vieux parchemin, où luisaient deux petits yeux noirs, durs comme des têtes de clous.`,
                `– Mais il n'a pas de barbe ! s'écria-t-il en levant au ciel ses deux mains sèches. On m'envoie un copiste qui n'a pas encore de barbe !`,
                `Mes jambes se mirent à trembler ; je me voyais déjà rentrant à Reims, sans un sou, comme un chien battu… Incapable d'articuler deux mots de suite, je fouillai dans ma poche et posai sur la table, toute froissée, la lettre d'introduction que m'avait confiée M. le proviseur.`,
                `Le bibliothécaire décacheta la lettre, l'approcha de la chandelle, l'en éloigna, la retourna, la renifla presque ; enfin il soupira que, puisque M. le proviseur me recommandait si chaudement, et que je sortais d'une famille des plus honorables, il voulait bien me prendre à l'essai, quoique je fusse, à son avis, beaucoup trop jeune. Suivit un long sermon sur les taches d'encre, dont je n'entendis pas un mot. On me gardait ! J'avais envie de rire et de pleurer à la fois, et j'aurais embrassé ce petit vieillard jaune, avec ses yeux de clous.`,
            ),
        },
        {
            page: 5,
            title: '',
            content: paragraphes(
                `Un claquement sec, tout près de mon oreille, coupa net ma joie. Derrière moi se tenait un grand homme maigre, entré sans bruit, avec un long cou de héron et une perruque couleur de paille : c'était l'archiviste, qui veillait sur les registres et sur ceux qui les recopiaient.`,
                `Il m'adressa un sourire d'une douceur extrême, tout en ouvrant et refermant sans cesse une tabatière d'argent, où il gardait son tabac à priser. J'aurais voulu me fier à ce sourire ; mais le couvercle retombait à chaque instant comme une petite mâchoire, clac ! clac ! clac !, et ce bruit me glaçait.`,
                `– Monsieur Brochard, voici notre nouveau copiste, annonça le bibliothécaire. Il prendra la place de M. Thévenin.`,
                `M. Brochard se cassa en deux pour me saluer. Mais sa tabatière, elle, claqua d'un petit air moqueur qui voulait dire clairement : « Ce gamin, à la place de M. Thévenin ? Laissez-moi rire ! »`,
                `Le bibliothécaire, à qui le message n'avait pas dû échapper, toussa et reprit : « M. Thévenin avait la plus belle écriture du département, et nous ne le remplacerons pas de sitôt… » À ces mots, la tabatière claqua si fort qu'on eût dit un éclat de rire. « Mais avec les conseils de M. Brochard, ajouta-t-il, ce jeune homme finira peut-être par écrire lisiblement. »`,
            ),
        },
        {
            page: 6,
            title: '',
            content: paragraphes(
                `M. Brochard, plus aimable encore, promit de me traiter comme son propre fils ; mais la tabatière, elle, ne promettait rien de bon. Elle s'ouvrait et se refermait de plus en plus vite, et ses claquements disaient : « Une seule tache d'encre, mon garçon, et je te croque tout cru ! »`,
                `– Monsieur Vasseur, dit enfin le bibliothécaire en reprenant sa plume, nous n'avons pas de lit pour vous ce soir : vous coucherez à l'auberge du Cheval-Blanc. Demain, à sept heures, je vous veux à cette table. Bonsoir.`,
                `Et, du bout de sa plume, il me montra la porte.`,
                `Au porche, M. Brochard, sans cesser de sourire, me tendit un papier plié en huit, couvert d'une écriture minuscule.`,
                `– Le tarif des amendes, murmura-t-il : un sou par tache d'encre, deux par mot raturé. Bonne nuit, mon jeune ami.`,
                `Le battant retomba derrière moi. Dans la rue noire, sous la pluie, il me sembla longtemps entendre encore, à travers le bois, le petit claquement sec de la tabatière.`,
                `Texte écrit pour TanoEd, à la manière d'un roman du XIXe siècle.`,
            ),
        },
    ],
};

// ─── Le second chemin : chaque question retrouve son appui dans le texte ───────────────────────────

const TEXTE = SUPPORT.pages.map((p) => p.content).join('\n\n');

/** Une question qui ne trouve plus son appui dans le texte lève une erreur au lieu de partir. */
const garde = (condition, message) => {
    if (!condition) throw new Error(`Items de français 4e, Petit Chose (variante) : ${message}`);
};

/** Le texte de la page n (1 à 6). */
const page = (n) => SUPPORT.pages[n - 1]?.content || '';

/** Un passage cité tel qu'il est écrit à la page qu'on indique à l'élève. */
const cite = (n, passage) => {
    garde(page(n).includes(passage), `« ${passage} » n'est pas à la page ${n}`);
    return passage;
};

/** Combien de fois un mot (un nom propre) apparait dans tout le texte. */
const occurrences = (mot) => TEXTE.split(mot).length - 1;

/** Le tableau Vrai / Faux des items Q03 et Q07 : colonne 0 = Vrai, 1 = Faux. */
const VRAI = 0;
const FAUX = 1;
const tableau = (lignes, bonnes, texte, label) =>
    makeVraiFaux(lignes, bonnes, texte, { ...opts(label), colonnes: ['Vrai', 'Faux'] });

/**
 * Q01 : Identifier le titre approprié.
 * Item : « Choisir le titre qui convient le mieux. » ; Entrée en fonction d'un jeune maitre d'étude
 *   (juste), Journée de rentrée au collège de Sarlande, Première nuit dans une petite ville des Cévennes,
 *   Arrivée du nouveau principal au collège.
 * Texte : tout l'extrait. Le narrateur se présente comme « le nouveau copiste » (page 2), le
 *   bibliothécaire le prend « à l'essai » (page 4) et l'attend « Demain, à sept heures » (page 6) :
 *   c'est son entrée en fonction.
 * Erreurs : « rentrée » : le bon lieu, mais l'évènement qu'on y attend et que le texte ne raconte pas (le
 *   portier prend le narrateur pour un élève) ; reproduit en « Journée de lecture à la bibliothèque
 *   d'Ombreval » (le portier : « Vous venez pour lire ? »). « Première nuit » : le cadre de la première
 *   phrase et une nuit annoncée, jamais racontée (coucher à l'hôtel) ; reproduit en « Première nuit dans
 *   une petite ville des Ardennes » (« vous coucherez à l'auberge du Cheval-Blanc »). « nouveau
 *   principal » : le nouveau venu confondu avec le chef qui l'accueille ; reproduit en « Arrivée du
 *   nouveau bibliothécaire d'Ombreval ».
 */
const L01 = 'Identifier le titre approprié';
const genQ01 = () => {
    garde(['Je suis le nouveau copiste', 'me prendre à l\'essai', 'Demain, à sept heures', 'Vous venez pour lire ?',
        'une petite ville des Ardennes', 'vous coucherez à l\'auberge'].every((appui) => TEXTE.includes(appui)), 'Q01, les appuis des titres');
    const juste = 'Entrée en fonction d\'un jeune copiste';
    const choix = [juste, 'Journée de lecture à la bibliothèque d\'Ombreval', 'Première nuit dans une petite ville des Ardennes',
        'Arrivée du nouveau bibliothécaire d\'Ombreval'];
    return avecSupportLecture(makeQcm(choix, juste, '<p><b>Choisir le titre qui convient le mieux.</b></p>', opts(L01)), SUPPORT);
};

/**
 * Q02 : Prélever une information secondaire littérale et immédiatement accessible.
 * Item : « Sarlande se trouve dans les… » ; Cévennes. (juste), Alpes., Vosges., Pyrénées.
 * Texte : page 1, première phrase du récit : « Ombreval est une petite ville des Ardennes ».
 * Erreurs : trois autres massifs de France, qu'aucun des deux textes ne cite : la réponse donnée au hasard,
 *   ou à l'idée d'un pays de montagnes et de forêts, sans retrouver la phrase ; gardés tels quels (Alpes,
 *   Vosges, Pyrénées). Les
 *   Cévennes ne sont pas reprises : chez l'élève qui se souvient de septembre, ce choix ne mesurerait
 *   plus la lecture.
 */
const L02 = 'Prélever une information littérale';
const genQ02 = () => {
    garde(page(1).includes('Ombreval est une petite ville des Ardennes'), 'Q02, la région de la ville');
    garde(['Alpes', 'Vosges', 'Pyrénées', 'Cévennes'].every((region) => !TEXTE.includes(region)), 'Q02, une autre région citée');
    const juste = 'Ardennes.';
    return avecSupportLecture(makeQcm([juste, 'Alpes.', 'Vosges.', 'Pyrénées.'], juste,
        '<p><b>Ombreval se trouve dans les…</b></p>', opts(L02)), SUPPORT);
};

/**
 * Q03 : Retrouver plusieurs informations secondaires explicites mais reformulées, situées dans le corps
 *   de l'extrait.
 * Item : « Le collège de Sarlande est… » ; tableau Vrai / Faux de quatre lignes : vivant. (Faux),
 *   ancien. (Vrai), grand. (Vrai), lumineux. (Faux).
 * Texte : « tout semblait endormi depuis cent ans » (page 2), « pas une voix, pas un bruit » (page 3) :
 *   pas vivante ; « creusés par les pas des siècles », une abbaye où des moines ont recopié des livres
 *   « pendant six cents ans » (page 3) : ancienne ; « Jamais je n'avais vu une maison aussi vaste » (page 2), « des
 *   couloirs qui n'en finissaient pas », « des salles hautes comme des églises » (page 3) : grande ;
 *   « pas une fenêtre éclairée » (page 2), « aux murs noircis », « une ombre que la lanterne perçait à
 *   peine » (page 3) : pas lumineuse. Aucun des quatre adjectifs n'est écrit dans le texte : chacun s'y
 *   retrouve reformulé, comme dans l'item.
 * Erreurs : vivant, lumineux : l'idée qu'on se fait du lieu (un collège, une bibliothèque) l'emporte sur
 *   ce que dit le texte ; ancien, grand : justes, mais jamais écrits tels quels. Les mêmes adjectifs,
 *   accordés à « la bibliothèque », les mêmes réponses.
 */
const L03 = 'Retrouver des informations reformulées';
const genQ03 = () => {
    const lignes = ['vivante.', 'ancienne.', 'grande.', 'lumineuse.'];
    const texteMinuscule = TEXTE.toLowerCase();
    garde(lignes.every((l) => !texteMinuscule.includes(l.slice(0, -1))), 'Q03, un adjectif écrit tel quel dans le texte');
    garde(['endormi depuis cent ans', 'pas une voix, pas un bruit', 'par les pas des siècles', 'pendant six cents ans',
        'une maison aussi vaste', 'des salles hautes comme des églises', 'pas une fenêtre éclairée', 'la lanterne perçait à peine']
        .every((appui) => TEXTE.includes(appui)), 'Q03, les appuis des quatre lignes');
    return avecSupportLecture(tableau(lignes, [FAUX, VRAI, VRAI, FAUX], '<p><b>La bibliothèque d\'Ombreval est…</b></p>', L03), SUPPORT);
};

/**
 * Q04 : Réaliser une inférence sur un court passage du texte.
 * Item : « Page 4 de la liseuse : « […], et je pus examiner à mon aise sa petite face pâlotte et sèche,
 *   éclairée par deux yeux froids, sans couleur. » Le narrateur parle… » ; du portier de l'internat.,
 *   du principal du collège. (juste), du surveillant général., du maitre d'étude.
 * Texte : page 4 : « Quand il eut signé sa page […], le bibliothécaire releva la tête, et je pus enfin
 *   voir son visage, jaune et ridé comme un vieux parchemin, […] » ; le début de la phrase, qui nomme le
 *   bibliothécaire, est caché par […], comme dans l'item.
 * Erreurs : le portier : le dernier personnage vu avant la citation (il sort à la fin de la page 3) ;
 *   reproduit en « du portier de la bibliothèque. ». Le surveillant général : l'autre personnage décrit
 *   de près, à la page suivante ; reproduit en « de l'archiviste. » (page 5). Le maitre d'études : le
 *   narrateur lui-même, celui qui regarde pris pour celui qu'il décrit ; reproduit en « du nouveau
 *   copiste. ».
 */
const L04 = 'Réaliser une inférence sur un passage';
const CITATION_Q04 = 'et je pus enfin voir son visage, jaune et ridé comme un vieux parchemin, où luisaient deux petits yeux noirs, durs comme des têtes de clous.';
const genQ04 = () => {
    const passage = cite(4, CITATION_Q04);
    garde(page(4).includes(`le bibliothécaire releva la tête, ${passage}`), 'Q04, le visage décrit est celui du bibliothécaire');
    const juste = 'du bibliothécaire de la ville.';
    const choix = ['du portier de la bibliothèque.', juste, 'de l\'archiviste.', 'du nouveau copiste.'];
    return avecSupportLecture(makeQcm(choix, juste,
        `<p>Page 4 de la liseuse : « […], ${passage} »</p><p><b>Le narrateur parle…</b></p>`, opts(L04)), SUPPORT);
};

/**
 * Q05 : Déduire l'émotion du narrateur à partir de l'attitude décrite dans un court passage donné.
 * Item : « « Je restai debout au milieu de la pièce, en tortillant mon chapeau entre mes doigts. »
 *   (page 3) Durant l'entretien avec le principal, le narrateur est… » ; rêveur., confiant.,
 *   angoissé. (juste), amusé.
 * Texte : page 3, dernière phrase : « Je restai debout près de la porte, immobile au bord du tapis, à
 *   tordre un bouton de ma veste, qui ne tenait plus qu'à un fil. » Un garçon qui n'ose pas avancer et
 *   tord un bouton jusqu'à l'arracher presque : l'émotion se déduit du geste, elle n'est pas nommée.
 * Erreurs : rêveur : l'immobilité lue comme une rêverie ; confiant, amusé : les émotions contraires. Les
 *   mêmes choix : ils ne nomment rien du texte.
 */
const L05 = 'Déduire une émotion d\'une attitude';
const CITATION_Q05 = 'Je restai debout près de la porte, immobile au bord du tapis, à tordre un bouton de ma veste, qui ne tenait plus qu\'à un fil.';
const genQ05 = () => {
    const passage = cite(3, CITATION_Q05);
    const juste = 'angoissé.';
    return avecSupportLecture(makeQcm(['rêveur.', 'confiant.', juste, 'amusé.'], juste,
        `<p>« ${passage} » (page 3)</p><p><b>Durant l'entretien avec le bibliothécaire, le narrateur est…</b></p>`, opts(L05)), SUPPORT);
};

/**
 * Q06 : Retrouver une information explicite mais reformulée, non immédiatement repérable car située en
 *   milieu de texte.
 * Item : « Le principal va embaucher le narrateur car il a été convaincu par… » ; la tenue qu'il
 *   porte., son jeune âge., la lettre qu'il remet. (juste), sa personnalité.
 * Texte : page 4 : la lettre d'introduction de M. le proviseur, posée sur la table, décachetée,
 *   retournée ; « puisque M. le proviseur me recommandait si chaudement […], il voulait bien me prendre
 *   à l'essai ».
 * Erreurs : la tenue : un détail d'apparence que le texte montre, sans rôle dans l'embauche (le manteau
 *   trop mince, page 1 ; le bouton qui ne tient plus qu'à un fil, page 3) ; le jeune âge : la raison
 *   contraire, ce qui fait hésiter le bibliothécaire (« Mais il n'a pas de barbe ! », « beaucoup trop
 *   jeune ») ; la personnalité : jamais jugée, le narrateur ne peut pas « articuler deux mots de
 *   suite ». Les mêmes choix.
 */
const L06 = 'Retrouver une information reformulée';
const genQ06 = () => {
    garde(['la lettre d\'introduction', 'me recommandait si chaudement', 'il voulait bien me prendre à l\'essai', 'beaucoup trop jeune',
        'manteau trop mince'].every((appui) => page(1).includes(appui) || page(4).includes(appui)), 'Q06, la lettre et les pièges');
    const juste = 'la lettre qu\'il remet.';
    return avecSupportLecture(makeQcm(['la tenue qu\'il porte.', 'son jeune âge.', juste, 'sa personnalité.'], juste,
        '<p><b>Le bibliothécaire va embaucher le narrateur car il a été convaincu par…</b></p>', opts(L06)), SUPPORT);
};

/**
 * Q07 : Mettre en relation des informations et établir des inférences.
 * Item : « « Le principal prit la lettre, la lut, la relut, la plia, la déplia, la relut encore, puis il
 *   finit par me dire que, grâce à la recommandation toute particulière du recteur et l'honorabilité de
 *   ma famille, il consentait à me prendre chez lui, bien que ma grande jeunesse lui fît peur. » La
 *   lettre d'introduction… » ; tableau Vrai / Faux : met en avant la jeunesse du narrateur. (Faux),
 *   présente les origines familiales du narrateur. (Vrai), critique le manque d'assurance du
 *   narrateur. (Faux), présente le narrateur de manière positive. (Vrai).
 * Texte : page 4 : « Le bibliothécaire décacheta la lettre, […] ; enfin il soupira que, puisque M. le
 *   proviseur me recommandait si chaudement, et que je sortais d'une famille des plus honorables, il
 *   voulait bien me prendre à l'essai, quoique je fusse, à son avis, beaucoup trop jeune. »
 * Erreurs : la jeunesse : écrite dans la phrase, mais c'est la crainte de celui qui lit la lettre, pas ce
 *   qu'elle dit (« à son avis ») ; le manque d'assurance : vrai du narrateur dans la scène (« Incapable
 *   d'articuler deux mots de suite », page 4), prêté à tort à la lettre. Les mêmes quatre lignes, les
 *   mêmes réponses.
 */
const L07 = 'Mettre en relation et inférer';
const CITATION_Q07 = 'Le bibliothécaire décacheta la lettre, l\'approcha de la chandelle, l\'en éloigna, la retourna, la renifla presque ; enfin il soupira que, puisque M. le proviseur me recommandait si chaudement, et que je sortais d\'une famille des plus honorables, il voulait bien me prendre à l\'essai, quoique je fusse, à son avis, beaucoup trop jeune.';
const genQ07 = () => {
    const passage = cite(4, CITATION_Q07);
    garde(page(4).includes('Incapable d\'articuler deux mots de suite'), 'Q07, le manque d\'assurance montré dans la scène');
    const lignes = [
        'met en avant la jeunesse du narrateur.',
        'présente les origines familiales du narrateur.',
        'critique le manque d\'assurance du narrateur.',
        'présente le narrateur de manière positive.',
    ];
    return avecSupportLecture(tableau(lignes, [FAUX, VRAI, FAUX, VRAI],
        `<p>« ${passage} »</p><p><b>La lettre d'introduction…</b></p>`, L07), SUPPORT);
};

/**
 * Q08 : Rendre compte d'une compréhension fine d'éléments implicites du texte, en distinguant ce qui
 *   relève de l'imaginaire du narrateur des éléments de réalité.
 * Item : « « mais les clés n'étaient pas bienveillantes, elles. Il fallait les entendre s'agiter et
 *   grincer avec frénésie : « Si tu bouges, petit drôle, gare à toi ! » » Les paroles soulignées
 *   sont… » (la menace soulignée) ; dites par le surveillant lui-même., pensées par le principal.,
 *   prononcées réellement par les clés., imaginées par le narrateur. (juste). La base coupe deux choix
 *   (« dites… . », « prononcées réellement… . ») : la capture fait foi.
 * Texte : page 6 : « mais la tabatière, elle, ne promettait rien de bon. Elle s'ouvrait et se refermait
 *   de plus en plus vite, et ses claquements disaient : « Une seule tache d'encre, mon garçon, et je te
 *   croque tout cru ! » » ; page 5, la tabatière « voulait dire clairement » sa première moquerie : c'est
 *   le narrateur qui prête des paroles à l'objet. La menace est soulignée (balise u), comme dans l'item.
 * Erreurs : le surveillant : il vient de parler, au discours indirect, juste avant ; reproduit en « dites
 *   par l'archiviste lui-même. » (« promit de me traiter comme son propre fils »). Le principal : le
 *   texte lui prête d'avoir compris ce que disaient les clés ; reproduit en « pensées par le
 *   bibliothécaire. » (« à qui le message n'avait pas dû échapper », page 5). Les clés : la
 *   personnification lue au pied de la lettre ; reproduit en « prononcées réellement par la tabatière. ».
 */
const L08 = 'Distinguer l\'imaginaire de la réalité';
const DEBUT_Q08 = 'mais la tabatière, elle, ne promettait rien de bon. Elle s\'ouvrait et se refermait de plus en plus vite, et ses claquements disaient :';
const PAROLES_Q08 = 'Une seule tache d\'encre, mon garçon, et je te croque tout cru !';
const genQ08 = () => {
    cite(6, `${DEBUT_Q08} « ${PAROLES_Q08} »`);
    garde(page(5).includes('qui voulait dire clairement') && page(5).includes('à qui le message n\'avait pas dû échapper')
        && page(6).includes('promit de me traiter comme son propre fils'), 'Q08, les appuis des pièges');
    const juste = 'imaginées par le narrateur.';
    const choix = ['dites par l\'archiviste lui-même.', 'pensées par le bibliothécaire.', 'prononcées réellement par la tabatière.', juste];
    return avecSupportLecture(makeQcm(choix, juste,
        `<p>« ${DEBUT_Q08} « <u>${PAROLES_Q08}</u> » »</p><p><b>Les paroles soulignées sont…</b></p>`, opts(L08)), SUPPORT);
};

/**
 * Q09 : Caractériser le lieu de l'action en s'appuyant sur l'atmosphère générale du texte.
 * Item : « Aux yeux du narrateur, l'atmosphère générale qui se dégage du collège est… » ; fantastique.,
 *   familière., accueillante., angoissante. (juste).
 * Texte : tout l'extrait : la pluie, la nuit, le froid, l'ombre, le silence (pages 1 à 3), la statue au
 *   visage rongé, la cloche « grincheuse », la porte qui s'ouvre seule, l'écho « comme si quelqu'un nous
 *   suivait », la tabatière « comme une petite mâchoire » ; la peur du narrateur (« Mes jambes se mirent
 *   à trembler », « ce bruit me glaçait »).
 * Erreurs : fantastique : la porte qui s'ouvre seule et les objets qui semblent parler font penser au
 *   surnaturel, mais rien n'arrive que le narrateur ne craigne ou n'imagine ; familière : le contraire,
 *   tout est nouveau pour le narrateur (l'archiviste promet de le traiter « comme son propre fils ») ;
 *   accueillante : les sourires et les promesses de l'archiviste, démentis par sa tabatière. Les mêmes
 *   choix, « du collège » devenu « de la bibliothèque ».
 */
const L09 = 'Caractériser le lieu par son atmosphère';
const genQ09 = () => {
    garde(['comme si quelqu\'un nous suivait', 'Mes jambes se mirent à trembler', 'ce bruit me glaçait', 'comme son propre fils']
        .every((appui) => TEXTE.includes(appui)), 'Q09, les appuis de l\'atmosphère');
    const juste = 'angoissante.';
    return avecSupportLecture(makeQcm(['fantastique.', 'familière.', 'accueillante.', juste], juste,
        '<p><b>Aux yeux du narrateur, l\'atmosphère générale qui se dégage de la bibliothèque est…</b></p>', opts(L09)), SUPPORT);
};

/**
 * Q10 : Comprendre la structure narrative du texte et la chaine anaphorique au sein des dialogues, afin
 *   de retrouver le nom du narrateur.
 * Item : « Le nom de famille du narrateur est… » ; Eyssette. (juste), Serrières., Viot., Cassagne.
 * Texte : page 6 : « – Monsieur Vasseur, dit enfin le bibliothécaire en reprenant sa plume, nous n'avons
 *   pas de lit pour vous ce soir » : le seul endroit où le nom du narrateur apparait, dans une réplique
 *   qui lui est adressée.
 * Erreurs : Serrières : le prédécesseur, le nom le plus cité (le narrateur vient le remplacer) ; reproduit
 *   en Thévenin (quatre fois). Viot : le surveillant général, présenté dans une réplique « Monsieur Viot,
 *   dit le principal » ; reproduit en Brochard (« Monsieur Brochard, voici notre nouveau copiste »).
 *   Cassagne : le portier, nommé une seule fois au détour du récit ; reproduit en Gobert.
 */
const L10 = 'Retrouver le nom du narrateur';
const NARRATEUR = 'Vasseur';
const AUTRES_NOMS = ['Thévenin', 'Brochard', 'Gobert'];
const genQ10 = () => {
    garde(occurrences(NARRATEUR) === 1 && page(6).includes(`– Monsieur ${NARRATEUR}, dit enfin le bibliothécaire`),
        'Q10, le nom du narrateur, une seule fois, dans une réplique qui lui est adressée');
    garde(occurrences('Thévenin') >= 3 && occurrences('Brochard') >= 2 && occurrences('Gobert') === 1, 'Q10, les noms des pièges');
    const juste = `${NARRATEUR}.`;
    return avecSupportLecture(makeQcm([juste, ...AUTRES_NOMS.map((nom) => `${nom}.`)], juste,
        '<p><b>Le nom de famille du narrateur est…</b></p>', opts(L10)), SUPPORT);
};

const GENERATORS = {
    q01_qcm_unique: genQ01,
    q02_qcm_unique: genQ02,
    q03_tableau_vf: genQ03,
    q04_qcm_unique: genQ04,
    q05_qcm_unique: genQ05,
    q06_qcm_unique: genQ06,
    q07_tableau_vf: genQ07,
    q08_qcm_unique: genQ08,
    q09_qcm_unique: genQ09,
    q10_qcm_unique: genQ10,
};

export const ITEMS = [
    { questionNum: 'Q01', formatKey: 'q01_qcm_unique', libelle: L01, bonneReponseOfficielle: 'Entrée en fonction d\'un jeune maitre d\'étude' },
    { questionNum: 'Q02', formatKey: 'q02_qcm_unique', libelle: L02, bonneReponseOfficielle: 'Cévennes.' },
    { questionNum: 'Q03', formatKey: 'q03_tableau_vf', libelle: L03, bonneReponseOfficielle: '[vivant.|Faux][ancien.|Vrai][grand.|Vrai][lumineux.|Faux]' },
    { questionNum: 'Q04', formatKey: 'q04_qcm_unique', libelle: L04, bonneReponseOfficielle: 'du principal du collège.' },
    { questionNum: 'Q05', formatKey: 'q05_qcm_unique', libelle: L05, bonneReponseOfficielle: 'angoissé.' },
    { questionNum: 'Q06', formatKey: 'q06_qcm_unique', libelle: L06, bonneReponseOfficielle: 'la lettre qu\'il remet.' },
    {
        questionNum: 'Q07',
        formatKey: 'q07_tableau_vf',
        libelle: L07,
        bonneReponseOfficielle: '[met en avant la jeunesse du narrateur.|Faux][présente les origines familiales du narrateur.|Vrai]'
            + '[critique le manque d’assurance du narrateur.|Faux][présente le narrateur de manière positive.|Vrai]',
    },
    { questionNum: 'Q08', formatKey: 'q08_qcm_unique', libelle: L08, bonneReponseOfficielle: 'imaginées par le narrateur.' },
    { questionNum: 'Q09', formatKey: 'q09_qcm_unique', libelle: L09, bonneReponseOfficielle: 'angoissante.' },
    { questionNum: 'Q10', formatKey: 'q10_qcm_unique', libelle: L10, bonneReponseOfficielle: 'Eyssette.' },
];

export const CATEGORIES = ITEMS.map(({ questionNum, formatKey, libelle }) => ({
    id: questionNum.toLowerCase(),
    label: `${questionNum} · ${libelle}`,
    formatsList: [{ key: formatKey, label: formatKey.includes('tableau') ? 'Tableau' : 'QCM' }],
}));

export const generate = (typeExercice) => {
    const key = GENERATORS[typeExercice] ? typeExercice : randomChoice(Object.keys(GENERATORS));
    const payload = GENERATORS[key]();
    payload._meta.generatorKey = key;
    return payload;
};
